/**
 * Optional system notification when a reply finishes while the tab is in the background.
 * Also registers the service worker (installable app, offline shell).
 * Switched on per device (localStorage). Uses the service worker when there is one, because
 * Android Chrome only shows notifications through a service worker.
 */

const KEY = "webthing.notifyOnDone";

export type NotifySupport = "ok" | "insecure" | "unsupported";

export function notifySupport(): NotifySupport {
  if (!("Notification" in window)) return "unsupported";
  // Browsers only allow notifications (and service workers) on https or localhost.
  if (!window.isSecureContext) return "insecure";
  return "ok";
}

export function notifyEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) === "1" && notifySupport() === "ok" && Notification.permission === "granted";
  } catch {
    return false;
  }
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

function serviceWorker(): Promise<ServiceWorkerRegistration | null> {
  registration ??= "serviceWorker" in navigator ? navigator.serviceWorker.register("/sw.js").catch(() => null) : Promise.resolve(null);
  return registration;
}

/** Asks for permission and switches notifications on. Returns the resulting permission. */
export async function enableNotify(): Promise<NotificationPermission | "unsupported"> {
  if (notifySupport() !== "ok") return "unsupported";
  const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  try {
    localStorage.setItem(KEY, permission === "granted" ? "1" : "0");
  } catch {
    /* storage unavailable */
  }
  if (permission === "granted") await serviceWorker();
  return permission;
}

export function disableNotify() {
  try {
    localStorage.setItem(KEY, "0");
  } catch {
    /* storage unavailable */
  }
}

export async function showNotification(title: string, body: string, url: string, tag: string, force = false) {
  if (!force && (!notifyEnabled() || !document.hidden)) return;
  if (notifySupport() !== "ok" || Notification.permission !== "granted") return;
  // The same tag from several open tabs shows a single notification.
  const options: NotificationOptions = { body: body.slice(0, 180), tag, icon: "/favicon.svg", data: { url } };
  const reg = await serviceWorker();
  if (reg) {
    await reg.showNotification(title, options);
    return;
  }
  const n = new Notification(title, options);
  n.onclick = () => {
    window.focus();
    location.assign(url);
    n.close();
  };
}

// The service worker also makes the app installable and lets it open offline (public/sw.js), so
// the built app always registers it; in development only when notifications need it.
if (import.meta.env.PROD || notifyEnabled()) void serviceWorker();
